import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { RefreshCw, Upload, Search, Inbox } from "lucide-react";
import Header from "../components/dashboard/Header";
import api, { formatApiErrorDetail } from "../lib/api";
import { ApSummary } from "../components/ap/ApSummary";
import { ApTable } from "../components/ap/ApTable";
import { ApDrawer } from "../components/ap/ApDrawer";
import { ApForgotten, forgetVendor } from "../components/ap/ApForgotten";
import { STATUS_META, inputCls } from "../components/ap/apUtils";

function InboxBar({ status, onSync, syncing, onUpload, uploading }) {
  const fileRef = useRef(null);
  return (
    <div className="flex flex-wrap items-center gap-3 border border-brand-border bg-brand-surface rounded-xl px-5 py-3" data-testid="ap-inbox-bar">
      <Inbox className="h-4 w-4 text-brand-tertiary" />
      <div className="text-sm flex-1 min-w-[220px]">
        <span className="text-brand-ink font-medium" data-testid="ap-mailbox">{status?.mailbox || "No billing inbox set"}</span>
        <span className="text-brand-muted"> · {status?.last_run ? `last checked ${new Date(status.last_run).toLocaleString()} (${status.last_added || 0} new)` : "not synced yet"} · pulls invoices from the last 2 weeks onward, auto-checks every 15 min</span>
        {status?.needs_review > 0 && <p className="text-xs text-amber-800 mt-0.5" data-testid="ap-review-count">{status.needs_review} item{status.needs_review === 1 ? "" : "s"} need review — filter by "Needs review".</p>}
        {status?.rejected_total > 0 && <p className="text-xs text-brand-tertiary mt-0.5" data-testid="ap-rejected-count">{status.rejected_total} non-invoice or duplicate attachment{status.rejected_total === 1 ? "" : "s"} filtered out.</p>}
        {status?.last_error && <p className="text-xs text-rose-700 mt-0.5" data-testid="ap-sync-error">Last sync error: {status.last_error.slice(0, 180)}</p>}
      </div>
      <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.heic" className="hidden" data-testid="ap-upload-input"
        onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) onUpload(f); }} />
      <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="ap-upload-btn"
        className="inline-flex items-center gap-1.5 border border-brand-border rounded-full px-4 py-2 text-sm text-brand-ink hover:bg-brand-hover transition-colors disabled:opacity-50">
        <Upload className="h-3.5 w-3.5" /> {uploading ? "Reading invoice…" : "Upload invoice"}
      </button>
      <button onClick={onSync} disabled={syncing} data-testid="ap-sync-btn"
        className="inline-flex items-center gap-1.5 bg-brand-ink text-brand-page rounded-full px-4 py-2 text-sm hover:opacity-90 transition-opacity disabled:opacity-50">
        <RefreshCw className={`h-3.5 w-3.5 ${syncing ? "animate-spin" : ""}`} /> {syncing ? "Checking inbox…" : "Check inbox now"}
      </button>
    </div>
  );
}

export default function AP() {
  const [status, setStatus] = useState(null);
  const [summary, setSummary] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [filters, setFilters] = useState({ status: "open", location: "", q: "" });
  const [openId, setOpenId] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [forgotKey, setForgotKey] = useState(0);

  const load = useCallback(async () => {
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    const [s, sum, inv] = await Promise.all([api.get("/ap/status"), api.get("/ap/summary"), api.get("/ap/invoices", { params })]);
    setStatus(s.data); setSummary(sum.data); setInvoices(inv.data);
  }, [filters]);
  useEffect(() => { load().catch((e) => toast.error("Could not load AP", { description: e.userMessage })); }, [load]);

  const sync = async () => {
    setSyncing(true);
    try { const { data } = await api.post("/ap/sync"); toast.success(`${data.added} new invoice${data.added === 1 ? "" : "s"} added`, { description: [data.dropped ? `${data.dropped} non-invoice/duplicate removed` : null, data.ramp_error].filter(Boolean).join(" · ") || undefined }); }
    catch (e) { toast.error("Inbox sync failed", { description: formatApiErrorDetail(e.response?.data?.detail), duration: 10000 }); }
    finally { setSyncing(false); load(); }
  };
  const upload = async (file) => {
    setUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post("/ap/invoices", fd); toast.success("Invoice added"); setOpenId(data.id); }
    catch (e) { toast.error("Upload failed", { description: formatApiErrorDetail(e.response?.data?.detail) }); }
    finally { setUploading(false); load(); }
  };
  const forget = async (inv) => { if (await forgetVendor(inv)) { setForgotKey((k) => k + 1); setOpenId(null); load(); } };
  const set = (k) => (e) => setFilters((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div className="min-h-screen bg-brand-page relative z-10">
      <Header />
      <main className="max-w-[1400px] mx-auto px-6 lg:px-12 py-10 space-y-8">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-brand-tertiary">Finance</p>
          <h1 className="font-serif text-4xl lg:text-5xl font-light tracking-tight text-brand-ink mt-2" data-testid="ap-title">Accounts Payable</h1>
          <p className="text-sm text-brand-muted mt-1">Invoices emailed to the billing inbox land here automatically. Track each one through to paid.</p>
        </div>
        <InboxBar status={status} onSync={sync} syncing={syncing} onUpload={upload} uploading={uploading} />
        <ApSummary summary={summary} />
        <div className="flex flex-wrap gap-3 items-center">
          <select value={filters.status} onChange={set("status")} data-testid="ap-filter-status" className={`${inputCls.replace("w-full ", "")} w-auto`}>
            <option value="open">All open</option><option value="review">Needs review</option><option value="">Everything</option>
            {Object.entries(STATUS_META).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
          </select>
          <select value={filters.location} onChange={set("location")} data-testid="ap-filter-location" className={`${inputCls.replace("w-full ", "")} w-auto`}>
            <option value="">All locations</option><option value="Unassigned">Unassigned</option>
            {(status?.locations || []).map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
          <div className="relative flex-1 min-w-[220px] max-w-sm">
            <Search className="h-3.5 w-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-brand-tertiary" />
            <input value={filters.q} onChange={set("q")} placeholder="Search vendor, invoice #, subject" data-testid="ap-search" className={`${inputCls} pl-8`} />
          </div>
          <div className="ml-auto"><ApForgotten refreshKey={forgotKey} onChanged={load} /></div>
        </div>
        <ApTable invoices={invoices} onOpen={setOpenId} onForget={forget} />
      </main>
      <ApDrawer id={openId} locations={status?.locations || []} onClose={() => setOpenId(null)} onChanged={load} onForget={forget} />
    </div>
  );
}
